/** PostHog, funnel events only (plan §5.11). No other event name may be added without updating the plan. */
import type { PostHog } from 'posthog-js';
import { config } from './config';

/** 'preview' on the workers.dev preview, 'production' on play.detectea.app (filter preview out in PostHog). */
const APP_ENV = (import.meta.env.VITE_APP_ENV as string | undefined) ?? (import.meta.env.DEV ? 'development' : 'production');

export type FunnelEvent =
  | 'app_opened' | 'anonymous_account_created' | 'first_launch_cold_open_started' | 'first_launch_cold_open_completed'
  | 'episode_started' | 'request_viewed' | 'request_completed' | 'hint_used'
  | 'proof_submitted' | 'text_answer_submitted' | 'choice_reply_submitted'
  | 'premium_gate_hit'
  | 'native_paywall_viewed' | 'native_paywall_dismissed' | 'native_paywall_cta_tapped'
  | 'native_purchase_started' | 'native_purchase_succeeded' | 'native_purchase_failed'
  | 'episode_solved' | 'language_selection_changed' | 'feedback_submitted' | 'support_chat_opened' | 'account_deleted';

/**
 * posthog-js (~95 KB gzip) is not on the critical path: it is imported after the first
 * render (window load + idle, or the first interaction / tab hide, whichever comes first).
 * Calls made before that are queued and replayed in order.
 */
type Call = (ph: PostHog) => void;
let enabled = false;
let client: PostHog | undefined;
let queue: Call[] = [];
let loading: Promise<void> | undefined;

function run(call: Call) {
  if (!enabled) return;
  if (client) call(client);
  else queue.push(call);
}

function load(): Promise<void> {
  loading ??= import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(config.posthogKey, { api_host: config.posthogHost, capture_pageview: false, autocapture: false, persistence: 'localStorage+cookie' });
      client = posthog;
      const pending = queue;
      queue = [];
      for (const call of pending) call(posthog);
    })
    .catch(() => { loading = undefined; });
  return loading;
}

function scheduleLoad() {
  if (typeof window === 'undefined') return;
  const triggers = ['pointerdown', 'keydown'] as const;
  const start = () => {
    for (const t of triggers) window.removeEventListener(t, start, true);
    document.removeEventListener('visibilitychange', onHide);
    void load();
  };
  const onHide = () => { if (document.visibilityState === 'hidden') start(); };
  for (const t of triggers) window.addEventListener(t, start, { capture: true, once: true, passive: true });
  document.addEventListener('visibilitychange', onHide);
  const idle = () => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 3000 });
    else setTimeout(start, 1500);
  };
  if (document.readyState === 'complete') idle();
  else window.addEventListener('load', idle, { once: true });
}

export function initAnalytics() {
  if (!config.posthogKey || enabled) return;
  enabled = true;
  scheduleLoad();
}

export function identify(userID: string, props: Record<string, string | boolean | number> = {}) {
  run((ph) => ph.identify(userID, { platform: 'web', app_env: APP_ENV, ...props }));
}

export function registerSuper(props: Record<string, string | boolean | number>) {
  run((ph) => ph.register({ platform: 'web', app_env: APP_ENV, ...props }));
}

export function track(event: FunnelEvent, props: Record<string, string | number | boolean | undefined> = {}) {
  if (config.isDev) console.debug('[analytics]', event, props);
  run((ph) => { ph.capture(event, props); });
}
